import { Button, Grid, Typography } from '@material-ui/core';
import { Field, Form, useField } from 'react-final-form';
import styled from 'styled-components';
import { COLOR_SUCCESSFUL } from '../../../../../../constants';
import { FormFormattedField } from '../../../../../../components';
import { required } from '../../../../../../utils/validators';
import { normalizeFormattedField, convertToLocalTime } from '../../../../../../utils';
import { User } from '../../../../../../types/user';
import { useActions } from './useActions';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import NumberFormat from 'react-number-format';
import { RenderType, useFormUiConfig } from './useFormUiConfig';
import { useLocalUpdates } from './useLocalUpdates';

const configByType = {
  bids: {
    title: 'All Bids',
    modalId: 'view-all-bids',
    editButton: 'Edit Bids',
    deleteButton: 'Delete all bids',
    log: 'Bid Log',
    confirmDeleteAll: 'Are you sure you want to remove all bids?'
  },
  offers: {
    title: 'All Offers',
    modalId: 'view-all-offers',
    editButton: 'Edit Offers',
    deleteButton: 'Delete all offers',
    log: 'Highest Offer',
    confirmDeleteAll: 'Are you sure you want to remove all offers?'
  },
  alternativeOffers: {
    title: 'All Offers',
    modalId: 'view-all-alternative-offers',
    editButton: 'Edit Offers',
    deleteButton: 'Delete all offers',
    log: 'Highest Offer',
    confirmDeleteAll: 'Are you sure you want to remove all offers?'
  }
};

const StyledForm = styled.div`
  @media only screen and (max-width: 820px) {
    width: 715px;
    display: block;
  }
`;

const StyledGrid = styled(Grid)`
  padding: 5px 0;
`;

const StyledFormFormattedField = styled(FormFormattedField)`
  max-width: 75px;
  input {
    color: ${COLOR_SUCCESSFUL};
  }
`;

const StyledTruncatedTypography = styled(Typography)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export type FormBid = {
  id: number;
  bid: number;
  bidder: string;
  date: string;
  created_at: string;
  admin: boolean;
};

type ProxyBid = {
  id: number;
  amount: number;
  bidder: User;
  created_at: string;
};

type Props = {
  bids: FormBid[];
  proxyBid?: ProxyBid;
  vehicleId: number;
  type: string;
  renderType?: RenderType;
};

export const EditBidsForm = ({ vehicleId, bids, proxyBid, type, renderType = 'modal' }: Props) => {
  const initialValues = {
    bids,
    proxy_bid_amount: proxyBid?.amount
  };

  const { formScroll } = useFormUiConfig(renderType);

  return (
    <Form onSubmit={() => {}} mutators={{ ...arrayMutators }} initialValues={initialValues}>
      {() => (
        <StyledForm style={formScroll}>
          {proxyBid && type !== 'alternativeOffers' && (
            <ProxyBid renderType={renderType} type={type} proxyBid={proxyBid} />
          )}
          <Typography>{configByType[type as keyof typeof configByType]?.log}:</Typography>
          <FieldArray name="bids">
            {({ fields }) =>
              fields.map((name, i) => (
                <Bid
                  vehicleId={vehicleId}
                  key={name}
                  name={name}
                  renderType={renderType}
                  type={type}
                  removable={i === 0 || type === 'alternativeOffers'}
                  editable={i === 0 || type === 'alternativeOffers'}
                />
              ))
            }
          </FieldArray>
        </StyledForm>
      )}
    </Form>
  );
};

type ProxyBidProps = {
  type: string;
  proxyBid: ProxyBid;
  renderType: RenderType;
};

const ProxyBid = ({ type, proxyBid, renderType }: ProxyBidProps) => {
  const { updateProxyBid, removeProxyBid } = useActions(type);

  const { buttonsXs, nameXs } = useFormUiConfig(renderType);

  const {
    input: { value: amount }
  } = useField(`proxy_bid_amount`);

  const saveProxy = () => updateProxyBid({ amount, id: proxyBid.id });

  return (
    <>
      <Typography>Proxy:</Typography>
      <StyledGrid spacing={16} container alignItems="center">
        <Grid item xs={2}>
          <Field
            name="proxy_bid_amount"
            placeholder="Enter Proxy Bid Amount"
            component={StyledFormFormattedField}
            thousandSeparator
            allowNegative={false}
            fixedDecimalScale
            prefix="$"
            disableUnderline
            validate={required}
            parse={normalizeFormattedField}
            hideErrors
          />
        </Grid>
        <Grid item xs={nameXs} title={proxyBid.bidder?.dealership_name}>
          <StyledTruncatedTypography>{`by ${proxyBid.bidder?.dealership_name}`}</StyledTruncatedTypography>
        </Grid>
        <Grid item xs={3}>
          <Typography>{convertToLocalTime(proxyBid.created_at, true)}</Typography>
        </Grid>
        <Grid
          item
          xs={buttonsXs}
          container
          justify="space-between"
          style={{ gap: 4, flexDirection: 'row', flexWrap: 'nowrap' }}
        >
          <Button style={{ flex: '1 1 50%' }} variant="contained" color="primary" onClick={saveProxy}>
            Save
          </Button>
          {type === 'bids' ? (
            <Button
              style={{ flex: '1 1 50%' }}
              variant="contained"
              color="secondary"
              onClick={() => {
                const isDeletingConfirmed = window.confirm(
                  `Are you sure that you want to delete ${proxyBid.bidder?.dealership_name}'s bid`
                );
                if (isDeletingConfirmed) {
                  removeProxyBid(proxyBid.id);
                }
              }}
            >
              Delete
            </Button>
          ) : (
            <div style={{ flex: '1 1 50%' }} />
          )}
        </Grid>
      </StyledGrid>
    </>
  );
};

type BidProps = {
  name: string;
  type: string;
  editable: boolean;
  removable: boolean;
  renderType: RenderType;
  vehicleId: number;
};

const Bid = ({ name, type, editable, removable, renderType, vehicleId }: BidProps) => {
  const {
    input: { value: bid }
  } = useField(name);

  const { removeBid, updateBid } = useActions(type);

  const { localSaveBid, localRemoveBid } = useLocalUpdates(type, vehicleId);

  const handleRemoveBid = async () => {
    const response = await removeBid(bid.id);

    if (type === 'alternativeOffers' && !('error' in response)) {
      localRemoveBid(bid);
    }
  };

  const saveBid = async () => {
    const response = await updateBid({ id: bid.id, amount: bid.bid });

    if (type === 'alternativeOffers' && !('error' in response)) {
      localSaveBid(bid);
    }
  };

  const { nameXs, buttonsXs } = useFormUiConfig(renderType);

  return (
    <StyledGrid spacing={16} container alignItems="center">
      <Grid item xs={2}>
        <BidField editable={editable} name={name} />
      </Grid>
      <Grid item xs={nameXs} title={bid.bidder}>
        <StyledTruncatedTypography>{`by ${bid.bidder}`}</StyledTruncatedTypography>
      </Grid>
      <Grid item xs={3}>
        <Typography>{bid.date}</Typography>
      </Grid>
      <Grid
        item
        xs={buttonsXs}
        container
        justify="space-between"
        style={{ gap: 4, flexDirection: 'row', flexWrap: 'nowrap' }}
      >
        <Button style={{ flex: '1 1 50%' }} disabled={!editable} variant="contained" color="primary" onClick={saveBid}>
          Save
        </Button>
        {type !== 'offers' && removable ? (
          <Button
            style={{ flex: '1 1 50%' }}
            variant="contained"
            color="secondary"
            onClick={() => {
              const isDeletingConfirmed = window.confirm(`Are you sure that you want to delete ${bid.bidder}'s bid`);
              if (isDeletingConfirmed) {
                handleRemoveBid();
                // initForm();
              }
            }}
          >
            Delete
          </Button>
        ) : (
          <div style={{ flex: '1 1 50%' }} />
        )}
      </Grid>
    </StyledGrid>
  );
};

const BidField = ({ editable, name }: Pick<BidProps, 'editable' | 'name'>) => {
  const {
    input: { value: bid }
  } = useField(name);

  if (!editable) {
    return <NumberFormat displayType="text" prefix="$" value={bid.bid} thousandSeparator />;
  }

  return (
    <Field
      name={`${name}.bid`}
      placeholder="Enter Bid Amount"
      component={StyledFormFormattedField}
      thousandSeparator
      allowNegative={false}
      fixedDecimalScale
      prefix="$"
      disableUnderline
      validate={required}
      parse={normalizeFormattedField}
      hideErrors
    />
  );
};
